import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsBoost from "highcharts/modules/boost";
import HighchartsExporting from "highcharts/modules/exporting";
import React, { FC, useEffect, useRef, useState } from "react";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";
import "./chartStyle.scss";

HighchartsBoost(Highcharts);
HighchartsExporting(Highcharts);
Exporting(Highcharts);
OfflineExporting(Highcharts);

interface CustomStackedChartCcomponentProps {
  chartTitle?: string;
  xAxisCategories: any;
  SeriesType: any;
  seriesData: any;
  seriesShowInLegend: boolean;
  isShowDataLevels: boolean;
  isShowStackedColor: boolean;
  seriesName?: string;
  xAxisTitleText?: string;
  yAxisTitleText?: string;
  baseLine?: { base: any; ranges: any };
  xAxisMin?: any;
  xAxisMax?: any;
  yAxisMin?: any;
  yAxisMax?: any;
  setSelectedLocation?: any;
  setActiveKey?: any;
  fileName?: string;
  chartName?: string;
  isNavigationActive?: boolean;
}

// outside the component
function tooltipFormatter(isShowStackedColor: boolean) {
  return function (this: Highcharts.TooltipFormatterContextObject): string {
    const tooltipId = "custom-tooltip-scroll";
    let tooltipContent = `<div id="${tooltipId}" class="custom-tooltip-scroll"><b>${this.x}</b><br/><br/>`;
    let total = 0;
    let inner = "";

    this?.points?.forEach((point) => {
      if (point.y !== 0) {
        const seriesName = point.series.name;
        const pointColor =
          typeof point.color === "string" ? point.color : "#148f77";
        const markerColor = isShowStackedColor ? pointColor : "#148f77";
        inner += `<span style="color:${markerColor}">\u25CF</span> ${seriesName}: ${point.y}<br/>`;
        total += point?.y || 0;
      }
    });

    const finalHtml = `${tooltipContent}${inner}<br/><b>Total: ${total}</b></div>`;

    setTimeout(() => {
      const tooltipEl = document.getElementById(tooltipId);
      if (tooltipEl) {
        const shouldScroll = tooltipEl.scrollHeight > 350;
        tooltipEl.style.overflowY = shouldScroll ? "auto" : "hidden";
      }
    }, 0);

    return finalHtml;
  };
}

function dataLabelFormatter(
  this: Highcharts.PointLabelObject,
): string | number {
  if (this.y === 0) {
    return `<span style="color: #999;opacity: 0">${this.y}</span>`;
  }
  return this.y ?? "";
}

const CustomStackedChartComponent: FC<CustomStackedChartCcomponentProps> = ({
  chartTitle,
  xAxisCategories,
  SeriesType,
  seriesData = [],
  seriesShowInLegend,
  isShowDataLevels = true,
  isShowStackedColor = true,
  seriesName,
  xAxisTitleText,
  yAxisTitleText,
  baseLine,
  xAxisMin,
  xAxisMax,
  yAxisMin,
  yAxisMax,
  setActiveKey,
  setSelectedLocation,
  fileName,
  chartName,
  isNavigationActive = false,
}) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [customHighChartOptions, setCustomHighChartOptions] = useState(
    {} as Highcharts.Options
  );

  useEffect(() => {
    if (seriesData.length) {
      const options: Highcharts.Options = {
        chart: {
          type: "column",
          style: {
            fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
          },
          zooming: {
            type: "x",
          },
          animation: {
            duration: 1500,
          },
        },
        credits: {
          enabled: false,
        },
        title: {
          text: chartTitle || "",
        },
        xAxis: [
          {
            categories: xAxisCategories,
            min: xAxisMin || 0,
            title: {
              text: xAxisTitleText || "",
              style: {
                fontSize: "16px",
                fontWeight: "bold",
                textAlign: "right",
              },
            },
          },
          {
            linkedTo: 0, // Link second axis to the first one
            opposite: true,
            visible: false,
          },
        ],
        yAxis: {
          min: yAxisMin || 0,
          title: {
            text: yAxisTitleText || "",
            style: {
              fontSize: "16px",
              fontWeight: "bold",
            },
          },
          stackLabels: {
            enabled: true,
          },
        },
        legend: {
          align: "center", // Align the legend horizontally to the center
          verticalAlign: "bottom", // Position the legend at the bottom
          layout: "horizontal", // Arrange items horizontally
          floating: false, // Make sure the legend is placed within the chart
          backgroundColor:
            Highcharts?.defaultOptions?.legend?.backgroundColor || "white",
          borderColor: "#CCC",
          borderWidth: 1,
          shadow: false,
        },

        tooltip: {
          shared: true,
          useHTML: true,
          formatter: tooltipFormatter(isShowStackedColor),
          style: {
            pointerEvents: "auto", // Ensure tooltips work on individual points
          },
        },

        plotOptions: {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: isShowDataLevels,
              formatter: dataLabelFormatter,
            },
            color: isShowStackedColor ? "" : "#66b2ff",
            pointWidth: 45,
            pointPadding: 0.2,
            groupPadding: 0.1,
            borderWidth: 1,
            borderColor: "#66b2ff", // Ensure the border lines are hidden
            states: {
              hover: {
                enabled: false,
              },
            },
            events: {
              click: function (event: any) {
                if (!isNavigationActive) return;

                const xLabel = event.point.category;
                const location = xLabel.split("_")[0];
                setSelectedLocation(location);
                setActiveKey("Sensor");
              },
            },
          },
        },
        series: seriesData.map((item: any) => ({
          type: SeriesType,
          name: item.name,
          data: item.data,
          showInLegend: seriesShowInLegend,
        })),
        accessibility: {
          enabled: false,
        },
        exporting: {
          enabled: true,
          fallbackToExportServer: false,
          filename: fileName || "chart",
          sourceWidth: 1200, // Increase source dimensions
          sourceHeight: 500,
          scale: 1, // Maintain original scale
          chartOptions: chartName
            ? {
                chart: {
                  height: "100%", // Use relative height
                  width: "100%", // Use relative width
                  spacingTop: 30, // Add more space at the top for the title
                  marginTop: 100, // Increase the top margin to push chart content down
                },
                title: {
                  text: chartName, // Dynamic title from prop
                  align: "center",
                  margin: 20,
                  style: {
                    fontSize: "24px",
                    fontWeight: "bold",
                  },
                },
              }
            : {
                chart: {
                  height: "100%", // Use relative height
                  width: "100%", // Use relative width
                },
                title: {
                  style: {
                    fontSize: "14px",
                  },
                },
              },
          buttons: {
            contextButton: {
              menuItems: [
                "viewFullscreen",
                "printChart",
                "separator",
                "downloadPNG",
                "downloadJPEG",
                "downloadPDF",
                "downloadSVG",
              ],
            },
          },
        },
      };

      setCustomHighChartOptions(options);
    }
  }, [seriesData, xAxisCategories, SeriesType, seriesShowInLegend]);

  return (
    <div>
      {chartTitle && (
        <h4
          style={{
            marginLeft: "1rem",
            zIndex: 999,
            position: "relative",
          }}
        >
          {chartTitle}
        </h4>
      )}
      {customHighChartOptions.series ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={customHighChartOptions}
          ref={chartComponentRef}
        />
      ) : (
        <div
          style={{
            height: "300px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D9D9D9",
            fontSize: "22px",
            fontWeight: "600",
            color: "#6d6d6d",
          }}
        >
          No Data Available
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomStackedChartComponent);
